import LegislativeEventsSearchParams from '@enview/interface/events/LegislativeEventsSearchParams';
import { SearchResult } from '@enview/interface/types/BillSearch';
import { JurisdictionReferenceInfo } from '@enview/interface/types/JurisdictionReferenceInfo';
import {
  LegislativeSessionWithReportMetadata,
  LegislativeSessionSummary,
} from '@enview/interface/src/types/LegislativeSession';
import { Hearing } from '@enview/interface/types/bills/Bill';
import base, { ApiTag } from './base';
import { Jurisdiction } from '@enview/interface/types/Jurisdiction';

export const CURRENT_SESSION_FILTER = 'Current Sessions';

const jurisdictionAPI = base.injectEndpoints({
  endpoints: (build) => ({
    // Returns the full list of US jurisdictions - does not require auth
    getJurisdictions: build.query<Jurisdiction[], void>({
      query: () => ({
        url: `/jurisdictions`,
        method: 'GET',
      }),
      providesTags: [{ type: ApiTag.DEFAULT_US_JURISDICTIONS }],
    }),
    // Parameter is a bit of a hack here - we always get the logged-in user but
    // orgUserId parameter gives us a convenient way to skip if we are logged-out
    getUserSupportedJurisdictions: build.query<Jurisdiction[], number | undefined>({
      query: (_orgUserId) => ({
        url: `/supported-jurisdictions`,
        method: 'GET',
      }),
      providesTags: [{ type: ApiTag.USER_JURISDICTIONS }],
    }),
    // SessionsWithReportMetadata includes end-of-session report data
    getSessionsWithReports: build.query<
      LegislativeSessionWithReportMetadata[],
      string[]
    >({
      query: (params) => {
        const jurisdictionParams = params.map(
          (jurisdiction) => `jurisdictionAbbreviations=${jurisdiction.toLowerCase()}`,
        );
        return {
          url: `/legislative-session-reports?${jurisdictionParams.join('&')}`,
          method: 'GET',
        };
      },
    }),
    // Returned objects will be LegislativeSessionSummary or LegislativeSesssionWithBillCount.
    // Bill count adds a noticeable latency cost across many jurisdictions
    getSessionSummaries: build.query<
      LegislativeSessionSummary[],
      { jurisdictions: string[]; withBillCounts: boolean }
    >({
      query: (param) => {
        const jurisdictionParams = param.jurisdictions.map(
          (jurisdiction) => `jurisdictions=${jurisdiction.toLowerCase()}`,
        );
        const billCountParam = param.withBillCounts
          ? `withBillCounts=true`
          : 'withBillCounts=false';
        return {
          url: `/legislative-session-summaries?${billCountParam}&${jurisdictionParams.join(
            '&',
          )}`,
          method: 'GET',
        };
      },
    }),
    getReferenceInfo: build.query<JurisdictionReferenceInfo, string>({
      query: (jurisAbbr) => {
        const juris = jurisAbbr.toLowerCase();
        return {
          url: `/jurisdictions/${juris}/reference-info`,
          method: 'GET',
        };
      },
    }),
    getEvents: build.query<SearchResult<Hearing>, LegislativeEventsSearchParams>({
      query: (search) => {
        return {
          url: `v2/legislative-events?${convertParamsToQuery(search)}`,
          method: 'GET',
        };
      },
    }),
  }),
  overrideExisting: false,
});

const convertParamsToQuery = (params: LegislativeEventsSearchParams): string => {
  const paramsToConvert = { ...params, committeeIds: null };
  const urlParams = Object.entries(paramsToConvert)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      const isDate = value instanceof Date;
      const formattedValue = isDate ? value.toISOString() : String(value);
      return `${encodeURIComponent(key)}=${encodeURIComponent(formattedValue)}`;
    });

  return urlParams.join('&');
};

export default jurisdictionAPI;
